import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { Job, JobAttempt, JobItem } from '@/api/types';
import { isTerminalJobStatus } from '@/utils/jobStatus';
import { jobDetailPollIntervalMs } from './useJobsDashboardPolling.utils';

type OpenJobData = {
  job: Job | null;
  items: JobItem[];
  attempts: JobAttempt[];
};

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Polls open job drawer detail (single job + items + attempts). List polling is useJobsListPolling. */
export function useJobsDashboardPolling(selectedJobId: string | null) {
  const [openJob, setOpenJob] = useState<OpenJobData>({ job: null, items: [], attempts: [] });
  const [openJobError, setOpenJobError] = useState<string | null>(null);
  const [openJobLoading, setOpenJobLoading] = useState(false);
  const [pollGeneration, setPollGeneration] = useState(0);
  const itemStatusFilterRef = useRef<string>('');
  const attemptStatusFilterRef = useRef<string>('');
  const abortRef = useRef<AbortController | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeJobRef = useRef(true);

  const loadOpenJob = useCallback(
    async (jobId: string, itemStatus?: string, attemptStatus?: string, signal?: AbortSignal) => {
      const itemFilter = itemStatus ?? itemStatusFilterRef.current;
      const attemptFilter = attemptStatus ?? attemptStatusFilterRef.current;
      const [job, items, attempts] = await Promise.all([
        api.jobs.get(jobId, { signal }),
        api.jobs.listItems(jobId, { status: itemFilter || undefined, limit: 200 }, { signal }),
        api.jobs.listAttempts(jobId, { status: attemptFilter || undefined, limit: 200 }, { signal }),
      ]);
      setOpenJob({ job, items, attempts });
      setOpenJobError(null);
      return { job, items, attempts };
    },
    []
  );

  const setItemStatusFilter = useCallback((status: string) => {
    itemStatusFilterRef.current = status;
  }, []);

  const setAttemptStatusFilter = useCallback((status: string) => {
    attemptStatusFilterRef.current = status;
  }, []);

  useEffect(() => {
    if (!selectedJobId) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      abortRef.current?.abort();
      setOpenJob({ job: null, items: [], attempts: [] });
      setOpenJobError(null);
      setOpenJobLoading(false);
      itemStatusFilterRef.current = '';
      attemptStatusFilterRef.current = '';
      return;
    }

    let cancelled = false;
    const jobId = selectedJobId;

    const scheduleNextPoll = (active: boolean, visible: boolean) => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      const delay = jobDetailPollIntervalMs(active, visible);
      if (delay <= 0) return;
      pollTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        void pollJob();
      }, delay);
    };

    async function pollJob() {
      if (cancelled) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const result = await loadOpenJob(jobId, undefined, undefined, controller.signal);
        if (cancelled || !result.job) return;
        const active = !isTerminalJobStatus(result.job.status);
        activeJobRef.current = active;
        scheduleNextPoll(active, document.visibilityState === 'visible');
      } catch (e) {
        if (cancelled || isAbortError(e) || controller.signal.aborted) return;
        setOpenJobError(e instanceof Error ? e.message : 'Failed to load job');
        scheduleNextPoll(true, document.visibilityState === 'visible');
      } finally {
        if (!cancelled) setOpenJobLoading(false);
      }
    }

    const onVisibilityChange = () => {
      if (cancelled) return;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      const visible = document.visibilityState === 'visible';
      if (visible) {
        void pollJob();
      } else {
        scheduleNextPoll(activeJobRef.current, false);
      }
    };

    setOpenJobLoading(true);
    itemStatusFilterRef.current = '';
    attemptStatusFilterRef.current = '';
    void pollJob();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      abortRef.current?.abort();
    };
  }, [selectedJobId, pollGeneration, loadOpenJob]);

  const refreshOpenJob = useCallback(
    async (itemStatus?: string, attemptStatus?: string) => {
      if (!selectedJobId) return null;
      setOpenJobLoading(true);
      try {
        return await loadOpenJob(selectedJobId, itemStatus, attemptStatus);
      } catch (e) {
        setOpenJobError(e instanceof Error ? e.message : 'Failed to load job');
        return null;
      } finally {
        setOpenJobLoading(false);
      }
    },
    [selectedJobId, loadOpenJob]
  );

  const restartJobPolling = useCallback(() => {
    setPollGeneration((g) => g + 1);
  }, []);

  return {
    openJob,
    openJobError,
    openJobLoading,
    refreshOpenJob,
    setItemStatusFilter,
    setAttemptStatusFilter,
    restartJobPolling,
  };
}
